import { match } from 'ts-pattern';
import { isClient } from '#/utils/ssr';

import config from '#/config';

export type CssVariable = (typeof variables)[number];

/**
 * Get a CSS variable.
 *
 * @param variable The CSS variable to get.
 * @returns The value of the CSS variable.
 */
export function cssVar(variable: CssVariable): string {
  if (!variables.includes(variable)) {
    throw new Error(`Invalid CSS variable: ${variable}`);
  }
  const value = cssVarsMap.get(variable);
  if (!value) {
    if (config.environment !== 'production') {
      // throw new Error(`CSS variable ${variable} not found`);
    }

    if (config.environment === 'production') {
      console.warn(`CSS variable ${variable} not found`);
    }

    return getVariableDefault(variable);
  }
  return value;
}

/*
|------------------
| Internals
|------------------
*/

const cssVarsMap = new Map<CssVariable, string>();

const variables = [
  /*
  |------------------
  | Base Colors
  |------------------
  */
  '--color-grey-50',
  '--color-grey-100',
  '--color-grey-200',
  '--color-grey-300',
  '--color-grey-400',
  '--color-grey-500',
  '--color-grey-600',
  '--color-grey-700',
  '--color-grey-800',
  '--color-grey-900',
  '--color-grey-950',

  /*
  |------------------
  | Breakpoints
  |------------------
  */

  '--breakpoint-xs',
  '--breakpoint-sm',
  '--breakpoint-md',
  '--breakpoint-lg',
  '--breakpoint-xl',
  '--breakpoint-2xl',

  /*
  |------------------
  | Colors
  |------------------
  */

  '--color-base-contrast',
  '--color-base-background',
  '--color-primary-light',
  '--color-primary-main',
  '--color-primary-dark',
  '--color-primary-contrast',
  '--color-secondary-light',
  '--color-secondary-main',
  '--color-secondary-dark',
  '--color-secondary-contrast',
  '--color-accent-light',
  '--color-accent-main',
  '--color-accent-dark',
  '--color-accent-contrast',
  '--color-neutral-light',
  '--color-neutral-main',
  '--color-neutral-dark',
  '--color-neutral-contrast',
  '--color-success-light',
  '--color-success-main',
  '--color-success-dark',
  '--color-success-contrast',
  '--color-error-light',
  '--color-error-main',
  '--color-error-dark',
  '--color-error-contrast',
  '--color-warning-light',
  '--color-warning-main',
  '--color-warning-dark',
  '--color-warning-contrast',
  '--color-info-light',
  '--color-info-main',
  '--color-info-dark',
  '--color-info-contrast',
  '--color-background-paper',
  '--color-background-default',
  '--color-divider',
] as const;

if (isClient()) {
  const root = document.documentElement;
  const styles = getComputedStyle(root);

  for (const variable of variables) {
    cssVarsMap.set(variable, getVariableFromStyles(styles, variable));
  }
}

function getVariableFromStyles(
  styles: CSSStyleDeclaration,
  variable: CssVariable
) {
  try {
    return styles.getPropertyValue(variable);
  } catch (error) {
    console.error(`Failed to load CSS variable ${variable}:`, error);
    return getVariableDefault(variable);
  }
}

function getVariableDefault(variable: CssVariable): string {
  return match(variable)
    .when(
      (v) => v.includes('color'),
      () => '#000'
    )
    .otherwise(() => '0');
}
